import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { FormGroup, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';

import { FormSubmitDirective } from 'ish-core/directives/form-submit.directive';
import { AccountFacade } from 'ish-core/facades/account.facade';
import { FeatureToggleService } from 'ish-core/feature-toggle.module';
import { Address } from 'ish-core/models/address/address.model';
import { FormlyAddressExtensionFormComponent } from 'ish-shared/formly-address-forms/components/formly-address-extension-form/formly-address-extension-form.component';
import { FormlyAddressFormComponent } from 'ish-shared/formly-address-forms/components/formly-address-form/formly-address-form.component';

/**
 * The Customer Address Form Component renders an address form with apply/cancel buttons so that the user can create or edit an address.
 * When the user submits the form the new/changed address will be sent to the parent component.
 *
 * @example
 * <ish-customer-address-form
      [address]="basket.invoiceToAddress"
      [resetForm]="resetForm"
      (save)="createCustomerInvoiceAddress($event)"
      (cancel)="cancelCreateCustomerInvoiceAddress()"
   ></ish-checkout-address-form>
 */
@Component({
  selector: 'ish-formly-customer-address-form',
  templateUrl: './formly-customer-address-form.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    ReactiveFormsModule,
    FormSubmitDirective,
    FormlyAddressFormComponent,
    FormlyAddressExtensionFormComponent,
    AsyncPipe,
    TranslateModule,
  ],
})
export class FormlyCustomerAddressFormComponent implements OnInit, OnChanges {
  private accountFacade = inject(AccountFacade);
  private featureToggleService = inject(FeatureToggleService);

  readonly address = input<Partial<Address>>(undefined);
  // not-dead-code
  readonly resetForm = input(false);
  // display address extension form fields
  readonly extension = input(false);

  readonly save = output<Address>();
  readonly cancel = output();

  form: FormGroup;
  extensionForm: FormGroup = new FormGroup({});

  extensionModel: { email: string };

  businessCustomer$: Observable<boolean>;

  readonly addressForm = viewChild<FormGroupDirective>('addressForm');

  get buttonLabel() {
    return Object.keys(this.address() ?? {}).length > 0
      ? 'account.addresses.update_address.button.label'
      : 'account.addresses.create_address.button.label';
  }

  ngOnInit() {
    this.businessCustomer$ = this.accountFacade.isBusinessCustomer$.pipe(
      withLatestFrom(this.accountFacade.isLoggedIn$),
      map(([isBusinessCustomer, isLoggedIn]) =>
        isBusinessCustomer || isLoggedIn
          ? isBusinessCustomer
          : this.featureToggleService.enabled('businessCustomerRegistration')
      )
    );

    // create form for creating a new address
    this.form = new FormGroup({
      address: new FormGroup({}),
      additionalAddressAttributes: this.extensionForm,
    });
  }

  /**
   * Trigger reset form from parent.
   */
  ngOnChanges(c: SimpleChanges) {
    this.doResetForm(c.resetForm?.currentValue);

    const address = this.address();
    this.extensionModel = address ? { email: address.email } : undefined;
  }

  private doResetForm(resetForm: boolean) {
    if (resetForm && this.form) {
      this.addressForm().resetForm();
      this.form.reset();
    }
  }

  submitForm() {
    // Mark all controls as touched to trigger validation errors
    this.form.markAllAsTouched();
    if (this.form.valid) {
      // build address from form data and send it to the parent
      let formAddress: Address = this.form.value.address;
      const address = this.address();
      if (address) {
        // update form values in the original address
        formAddress = { ...address, mainDivisionCode: '', ...formAddress };
      }
      if (this.extension()) {
        formAddress = { ...formAddress, email: this.extensionForm.get('email')?.value };
      }
      this.save.emit(formAddress);
    }
  }

  cancelForm() {
    this.cancel.emit();
  }
}
